/**
 * Modal — accessible centered modal with backdrop.
 *
 * Closes on:
 *   - Escape key
 *   - Backdrop click (unless `dismissible={false}`)
 *   - Programmatic call to onClose
 *
 * Accessibility:
 *   - Focus moves into the dialog on open and is trapped (Tab cycles).
 *   - Focus returns to the previously-focused element on close.
 *
 * Props:
 *   open        — required boolean
 *   onClose     — required handler
 *   title       — optional header text
 *   children    — modal body
 *   footer      — optional footer (e.g. action buttons)
 *   size        — 'sm' | 'md' | 'lg' (default 'md')
 *   dismissible — clicking backdrop closes the modal (default true)
 */

import { useEffect, useRef } from 'react';
import Icon from './Icons.jsx';

const SIZE_CLASSES = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
};

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  dismissible = true,
}) {
  const cardRef = useRef(null);
  const restoreFocusRef = useRef(null);

  // Close on Escape + trap Tab focus inside the dialog
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape' && dismissible) {
        onClose?.();
        return;
      }
      if (e.key !== 'Tab' || !cardRef.current) return;

      const focusables = cardRef.current.querySelectorAll(FOCUSABLE);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, dismissible, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Move focus in on open; restore it on close
  useEffect(() => {
    if (!open) return undefined;
    restoreFocusRef.current = document.activeElement;

    // Focus the first form control (or the card itself as fallback)
    const id = requestAnimationFrame(() => {
      const target =
        cardRef.current?.querySelector('input, select, textarea') ||
        cardRef.current?.querySelector(FOCUSABLE) ||
        cardRef.current;
      target?.focus?.();
    });

    return () => {
      cancelAnimationFrame(id);
      restoreFocusRef.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  const sizeCls = SIZE_CLASSES[size] || SIZE_CLASSES.md;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm orin-anim-fade"
        onClick={dismissible ? onClose : undefined}
        aria-hidden="true"
      />

      {/* Card */}
      <div
        ref={cardRef}
        tabIndex={-1}
        className={`relative w-full ${sizeCls} bg-card rounded-lg shadow-lg border border-border-base flex flex-col max-h-[90vh] orin-anim-pop`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {(title || dismissible) && (
          <div className="flex items-center justify-between px-5 py-4 border-b border-border-base flex-shrink-0">
            {title && (
              <h2 id="modal-title" className="text-base font-semibold text-ink">
                {title}
              </h2>
            )}
            {dismissible && (
              <button
                type="button"
                onClick={onClose}
                className="text-muted hover:text-ink transition-colors p-1 -m-1 rounded"
                aria-label="Close"
              >
                <Icon name="close" size={18} />
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto flex-1">{children}</div>

        {/* Footer */}
        {footer && (
          <div className="px-5 py-3 border-t border-border-base bg-slate-50/50 flex items-center justify-end gap-2 flex-shrink-0 rounded-b-lg">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
